import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Button,
  Form,
  Col,
  Row,
  Spinner,
  Alert,
} from "react-bootstrap";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import {
  getChangeLog,
} from "../../redux/slices/postChangeLogSlice";
import TopNav from "../TopNav/TopNav";
import JobNav from "../JobDetailPage/JobNav";
import JobGeneralOverview from "../JobDetailPage/JobOverviewBar";
import CheckoutNotification from "../JobDetailPage/checkoutNotification";
import { selectUserDetails } from "../../redux/slices/userSlice";
import { diffWords } from "diff";
import { postChangeOrderDetail } from "../../redux/slices/postChangeOrderDetailSlice";
import { selectJobId, selectGeneralProject } from "../../redux/selectors";
import useJobHandler from "../../functions/JobHandler";
import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import { replaceRealtimeChangeLog } from '../../redux/slices/postChangeLogSlice';
import * as XLSX from "xlsx";
import { usePermissions } from "../../hooks/usePermissions";

const ChangeLog = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const jobId = useSelector(selectJobId);
  const projectDetails = useSelector(selectGeneralProject);
    useJobHandler();
  
  useEffect(() => {
    if (jobId) {
      console.log(`Job ID from Redux: ${jobId}`);
      dispatch(getChangeLog(jobId));
    }
  }, [dispatch, jobId]);

  const userDetails = useSelector(selectUserDetails);
  const currentUser = userDetails?.name;
  const { canEdit } = usePermissions();
  const canAddToChangeOrder = canEdit('portal.change-log.add-to-change-order');

  // Use the selector that gets the change logs (already filtered by job ID on the server)
  const changeLogs = useSelector((state) => state.postChangeLog.changeLogs || []);
  const isLoading = useSelector((state) => state.postChangeLog.status === "loading");
  const error = useSelector((state) => state.postChangeLog.error);
  useGuardedRealtimeList({ apiKey: 'changeLog', params: { projectID: jobId }, enabled: Boolean(jobId), blocked: isLoading,
    apply: rows => dispatch(replaceRealtimeChangeLog(rows)) });

  const [filteredLog, setFilteredLog] = useState([]);
  const [userFilter, setUserFilter] = useState(null);
  const [sectionFilter, setSectionFilter] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [highlightComments, setHighlightComments] = useState(false);
  const [addStatus, setAddStatus] = useState(null);
  const [isCompactChangeLog, setIsCompactChangeLog] = useState(false);
  const [isMobileChangeLog, setIsMobileChangeLog] = useState(false);
  const [areFiltersExpanded, setAreFiltersExpanded] = useState(false);
  const addStatusTimeoutRef = useRef(null);

  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [uniqueSections, setUniqueSections] = useState([]);
  const [uniqueRoles, setUniqueRoles] = useState([]);

  useEffect(() => {
    return () => {
      if (addStatusTimeoutRef.current) {
        clearTimeout(addStatusTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const compactMediaQuery = window.matchMedia(
      "(max-width: 767.98px), (min-width: 768px) and (max-width: 1366px) and (hover: none) and (pointer: coarse)"
    );
    const mobileMediaQuery = window.matchMedia("(max-width: 767.98px)");

    const handleCompactChange = (event) => {
      setIsCompactChangeLog(event.matches);
    };

    const handleMobileChange = (event) => {
      setIsMobileChangeLog(event.matches);
      setAreFiltersExpanded(!event.matches);
    };

    handleCompactChange(compactMediaQuery);
    handleMobileChange(mobileMediaQuery);
    compactMediaQuery.addEventListener("change", handleCompactChange);
    mobileMediaQuery.addEventListener("change", handleMobileChange);

    return () => {
      compactMediaQuery.removeEventListener("change", handleCompactChange);
      mobileMediaQuery.removeEventListener("change", handleMobileChange);
    };
  }, []);

  const formatSectionName = (section) => {
    if (!section) return ""; // Add this line to handle null or undefined section
    return section
      .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before uppercase letters
      .replace(/^[a-z]/, (match) => match.toUpperCase()); // Capitalize the first letter
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "BLANK";
    }
    if (value === false || value === "false" || value === "Off" || value === "No") {
      return "No";
    }
    if (value === true || value === "true" || value === "On" || value === "Yes") {
      return "Yes";
    }
    return value;
  };

  // Extract unique filter values
  useEffect(() => {
    if (changeLogs.length) {
      setUniqueUsers([...new Set(changeLogs.map((item) => item.CurrentUser))]);
      setUniqueSections([
        ...new Set(changeLogs.map((item) => formatSectionName(item.Section))),
      ]);
      setUniqueRoles([...new Set(changeLogs.map((item) => item.UserRole))]);
    }
  }, [changeLogs]);

  useEffect(() => {
    if (!changeLogs.length) return;

    // Sort changeLogs by DateTime descending to have the most recent at the top
    let sortedLog = [...changeLogs].sort(
      (a, b) => new Date(b.DateTime) - new Date(a.DateTime)
    );

    // Apply client-side filters (no need to filter by jobId here since the API already did that)
    let filtered = sortedLog;

    if (userFilter) {
      filtered = filtered.filter((item) => item.CurrentUser === userFilter);
    }

    if (sectionFilter) {
      filtered = filtered.filter(
        (item) => formatSectionName(item.Section) === sectionFilter
      );
    }

    if (roleFilter) {
      filtered = filtered.filter((item) => item.UserRole === roleFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter((item) =>
        Object.values(item).some((value) =>
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (dateRange[0] && dateRange[1]) {
      filtered = filtered.filter((item) => {
        const itemDate = moment.utc(item.DateTime);
        const startDate = moment.utc(dateRange[0], "YYYY-MM-DD").startOf("day");
        const endDate = moment.utc(dateRange[1], "YYYY-MM-DD").endOf("day");
        return itemDate.isBetween(startDate, endDate, "minutes", "[]");
      });
    }

    setFilteredLog(filtered);
  }, [
    changeLogs,
    userFilter,
    sectionFilter,
    roleFilter,
    searchTerm,
    dateRange,
  ]);

  const highlightNewValue = (PrevValue, NewValue) => {
    if (!highlightComments) {
      return NewValue;
    }

    if (!PrevValue && NewValue) {
      return <span style={{ backgroundColor: "lightgreen" }}>{NewValue}</span>;
    }

    if (PrevValue && NewValue) {
      const diff = diffWords(PrevValue, NewValue);
      return diff.map((part, index) => {
        if (part.added) {
          return (
            <span key={index} style={{ backgroundColor: "lightgreen" }}>
              {part.value}
            </span>
          );
        } else if (!part.removed) {
          return <span key={index}>{part.value}</span>;
        }
        return null;
      });
    }

    return null;
  };

  const highlightPrevValue = (PrevValue, NewValue) => {
    if (!highlightComments) {
      return PrevValue;
    }

    if (PrevValue && !NewValue) {
      return <span style={{ backgroundColor: "salmon" }}>{PrevValue}</span>;
    }

    if (PrevValue && NewValue) {
      const diff = diffWords(PrevValue, NewValue);
      return diff.map((part, index) => {
        if (part.removed) {
          return (
            <span key={index} style={{ backgroundColor: "salmon" }}>
              {part.value}
            </span>
          );
        } else if (!part.added) {
          return <span key={index}>{part.value}</span>;
        }
        return null;
      });
    }

    return null;
  };

  const handleAddToChangeOrder = async (item) => {
    const changeOrderDetail = {
      projectID: item.ProjectID,
      currentUser: item.CurrentUser,
      userRole: item.UserRole,
      section: item.Section,
      field: item.Field,
      prevValue: item.PrevValue,
      existingValue: item.PrevValue,
      newValue: item.NewValue,
      dateTime: item.DateTime,
      title: `${item.Field} (${item.Section})`,
      status: "Pending",
    };

    if (addStatusTimeoutRef.current) {
      clearTimeout(addStatusTimeoutRef.current);
    }

    try {
      await dispatch(postChangeOrderDetail(changeOrderDetail)).unwrap();
      setAddStatus({ type: "success", message: "Added to Change Orders." });
    } catch (err) {
      const errorMessage = typeof err === "string" ? err : err?.message;
      setAddStatus({ type: "danger", message: errorMessage || "Unable to add change order." });
    } finally {
      addStatusTimeoutRef.current = setTimeout(() => setAddStatus(null), 4000);
    }
  };

  const handleExportToExcel = () => {
    // Get job details for header row
    const jobDetails = projectDetails.length > 0 ? projectDetails[0] : {};
    const jobNumber = jobDetails.JobNumber || jobId || "Unknown";
    const projectName = jobDetails.ProjectName || "Unknown Project";
    
    // Prepare data for export - strip HTML formatting and get clean text
    const exportData = filteredLog.map((item) => ({
      "Date/Time": moment(item.DateTime).format("MM/DD/YYYY hh:mm A"),
      "User": formatValue(item.CurrentUser),
      "Role": formatValue(item.UserRole),
      "Section": formatSectionName(item.Section),
      "Field": formatValue(item.Field),
      "Previous Value": formatValue(item.PrevValue),
      "New Value": formatValue(item.NewValue),
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    
    // Create header data with job info
    const headerData = [
      [`${jobNumber} - ${projectName}`], // Job info row
      [], // Empty row for spacing
      ["Date/Time", "User", "Role", "Section", "Field", "Previous Value", "New Value"] // Column headers
    ];
    
    // Convert header to worksheet format
    const worksheet = XLSX.utils.aoa_to_sheet(headerData);
    
    // Add the actual data starting from row 4 (index 3)
    XLSX.utils.sheet_add_json(worksheet, exportData, { 
      origin: "A4", 
      skipHeader: true 
    });

    // Auto-size columns
    const colWidths = [
      { wch: 18 }, // Date/Time
      { wch: 20 }, // User
      { wch: 15 }, // Role
      { wch: 20 }, // Section
      { wch: 25 }, // Field
      { wch: 30 }, // Previous Value
      { wch: 30 }, // New Value
    ];
    worksheet["!cols"] = colWidths;

    // Merge cells for the header row to span all columns
    worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];
    
    // Style the header row (bold and centered)
    if (worksheet["A1"]) {
      worksheet["A1"].s = {
        font: { bold: true, size: 14 },
        alignment: { horizontal: "center" }
      };
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Change Log");

    // Generate filename with job number and current date
    const filename = `ChangeLog_${jobNumber}_${moment().format("YYYY-MM-DD")}.xlsx`;

    // Save file
    XLSX.writeFile(workbook, filename);
  };

  const activeFilterCount = [
    userFilter,
    sectionFilter,
    roleFilter,
    searchTerm,
    dateRange[0],
    dateRange[1],
    highlightComments,
  ].filter(Boolean).length;

  return (
    <div>
      {!embedded && (
        <>
          <TopNav />
          <JobGeneralOverview />
          <CheckoutNotification currentUser={currentUser} />
          <JobNav keyValue="additionalItems" />
        </>
      )}

      <div className="p-3 change-log-filter-panel">
        {isMobileChangeLog && (
          <Button
            type="button"
            variant="outline-secondary"
            className="change-log-filter-toggle"
            aria-expanded={areFiltersExpanded}
            onClick={() => setAreFiltersExpanded((expanded) => !expanded)}
          >
            Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
          </Button>
        )}
        <div
          className={`change-log-filter-body${
            isMobileChangeLog && !areFiltersExpanded ? " change-log-filter-body-collapsed" : ""
          }`}
        >
          <Form className="change-log-filter-form">
            <Row className="g-3 change-log-filter-row">
              {/* Filters Row */}
              <Col>
                <Form.Group controlId="userFilter">
                  <Form.Label>User Filter</Form.Label>
                  <Form.Select
                    onChange={(e) => setUserFilter(e.target.value)}
                    value={userFilter || ""}
                  >
                    <option value="">Select User</option>
                    {uniqueUsers.map((user, index) => (
                      <option key={index} value={user}>
                        {user}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="sectionFilter">
                  <Form.Label>Section Filter</Form.Label>
                  <Form.Select
                    onChange={(e) => setSectionFilter(e.target.value)}
                    value={sectionFilter || ""}
                  >
                    <option value="">Select Section</option>
                    {uniqueSections.map((section, index) => (
                      <option key={index} value={section}>
                        {section}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="roleFilter">
                  <Form.Label>Role Filter</Form.Label>
                  <Form.Select
                    onChange={(e) => setRoleFilter(e.target.value)}
                    value={roleFilter || ""}
                  >
                    <option value="">Select Role</option>
                    {uniqueRoles.map((role, index) => (
                      <option key={index} value={role}>
                        {role}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            {/* Date Range and Search Row */}
            <Row className="g-3 mt-3 change-log-filter-row">
              <Col>
                <Form.Group controlId="dateRangeStart">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateRange[0] ? dateRange[0] : ""}
                    onChange={(e) => setDateRange([e.target.value, dateRange[1]])}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="dateRangeEnd">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateRange[1] ? dateRange[1] : ""}
                    onChange={(e) => setDateRange([dateRange[0], e.target.value])}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-3 mt-3 align-items-center change-log-action-row">
              <Col className="d-flex align-items-center">
                <Form.Group controlId="searchTerm" className="w-100">
                  <Form.Label className="me-2 mb-0">Search</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search all fields"
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="highlightComments">
                  <Form.Check
                    type="checkbox"
                    label="Highlight Changes in Comments"
                    checked={highlightComments}
                    onChange={(e) => setHighlightComments(e.target.checked)}
                  />
                </Form.Group>
              </Col>
              {!isCompactChangeLog && (
                <Col className="d-flex justify-content-end change-log-export-col">
                  <Button
                    variant="success"
                    onClick={handleExportToExcel}
                    disabled={filteredLog.length === 0}
                    className="ms-2 change-log-export-button"
                  >
                    Export to Excel
                  </Button>
                </Col>
              )}
            </Row>
          </Form>
        </div>
        {addStatus && (
          <Alert
            variant={addStatus.type}
            dismissible
            onClose={() => setAddStatus(null)}
            className="mt-3 mb-0"
          >
            {addStatus.message}
          </Alert>
        )}
      </div>
      <div className="p-3 change-log-results">
          <span className="change-log-total">Total Items: {filteredLog.length}</span>
        {isLoading ? (
          <Spinner animation="border" />
        ) : error ? (
          <div>Error loading change logs</div>
        ) : filteredLog.length === 0 ? (
          <div>No changes found</div>
        ) : (
          <div className="change-log-table-shell" style={{ height: "600px", overflowY: "auto", position: "relative" }}>
            <Table
              striped
              bordered
              hover
              className="mt-3 change-log-table"
              style={{ border: "2px solid #dee2e6" }}
            >
              <thead>
                <tr>
                  <th style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 10 }}>Date/Time</th>
                  <th style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 10 }}>User</th>
                  <th style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 10 }}>Field</th>
                  <th style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 10 }}>Previous Value</th>
                  <th style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 10 }}>New Value</th>
                  {canAddToChangeOrder && <th style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 10 }}>Action</th>}
                </tr>
              </thead>
              <tbody>
                {filteredLog.map((item, index) => (
                  <tr key={index}>
                    <td data-label="Date/Time">
                      {moment(item.DateTime).format("MM/DD/YYYY hh:mm A")}
                    </td>
                    <td data-label="User">{formatValue(item.CurrentUser)}</td>
                    <td data-label="Field">{formatValue(item.Field)}</td>
                    <td data-label="Previous Value">
                      {item.Field && item.Field.toLowerCase().includes("comment")
                        ? highlightPrevValue(item.PrevValue, item.NewValue)
                        : formatValue(item.PrevValue)}
                    </td>
                    <td data-label="New Value">
                      {item.Field && item.Field.toLowerCase().includes("comment")
                        ? highlightNewValue(item.PrevValue, item.NewValue)
                        : formatValue(item.NewValue)}
                    </td>
                    {canAddToChangeOrder && (
                      <td data-label="Action" className="change-log-action-cell">
                        <Button
                          variant="primary"
                          size="sm"
                          className="change-log-add-button"
                          onClick={() => handleAddToChangeOrder(item)}
                        >
                          +
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangeLog;
